import {
  Box,
  Accordion,
  Typography,
  AccordionDetails,
  AccordionSummary,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { components } from '@common/types';
import { ComponentMapper } from 'components/DynamicFormFields/FormMapper';
import { theme } from '@store';
import { useFormContext } from 'react-hook-form';
import { parseConditional } from '@util/dynamicFormParseConditional.ts';

export const RequirementItem = ({
  title,
  label,
  subtitle,
  editable = true,
  disable_if,
  description,
  handleChangeField,
  hide_if,
  no_bottom_gutters,
  question,
  ...props
}: components['schemas']['RequirementResponse'] & {
  patient_id?: string;
  question?: string;
  handleChangeField?: (nameList: string[]) => void;
}) => {
  const { watch } = useFormContext();
  const formValues = watch();
  const isDisabled = !editable;

  const isHidden = parseConditional(hide_if, formValues);

  const componentWrapper = (
    <Box sx={{ width: '100%' }}>
      <ComponentMapper
        label={label}
        name={props.field_name}
        enum_options={props.enum_options}
        disable_if={disable_if}
        patient_id={props.patient_id}
        disabled={isDisabled}
        onChangeCallback={handleChangeField}
        {...props}
      />
      {subtitle && !question && (
        <Typography
          variant='caption'
          sx={{
            color: 'text.secondary',
            display: 'block',
            mt: 0.5,
            ml: 1,
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        width: '100%',
        display: isHidden ? 'none' : 'block',
        mb: no_bottom_gutters ? -3 : 0,
      }}
    >
      {description && title ? (
        <Accordion
          sx={{
            width: '100%',
            '&::before': {
              display: 'none',
            },
          }}
          elevation={0}
          disableGutters
        >
          <AccordionSummary
            sx={{
              pl: 0,
              pb: 0,
              margin: 0,
              minHeight: 'unset',
              height: '28px',
              '& .MuiAccordionSummary-content': {
                margin: 0,
              },
              '& .MuiAccordionSummary-expandIconWrapper': {
                mr: -1,
              },
            }}
            expandIcon={<ExpandMoreIcon />}
          >
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography variant='body1' sx={{ fontSize: '1rem' }}>
                {title}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              bgcolor: theme.palette.grey[100],
              color: theme.palette.semiBlack.main,
              py: '1rem',
            }}
          >
            <Typography sx={{ whiteSpace: 'pre-line' }}>
              {description}
            </Typography>
          </AccordionDetails>
        </Accordion>
      ) : description ? (
        <Accordion
          sx={{
            width: '100%',
            '&::before': {
              display: 'none',
            },
          }}
          elevation={0}
          disableGutters
        >
          <AccordionSummary
            sx={{
              px: 0,
              pb: 0,
              margin: 0,
              minHeight: 'unset',
              height: '28px',
              '& .MuiAccordionSummary-content': {
                margin: 0,
              },
              '& .MuiAccordionSummary-expandIconWrapper': {
                mr: 1,
              },
            }}
            expandIcon={<ExpandMoreIcon />}
          >
            <Box
              sx={{ width: '100%' }}
              onClick={event => event.stopPropagation()}
              onFocusCapture={event => event.stopPropagation()}
              onKeyDown={event => event.stopPropagation()}
            >
              {componentWrapper}
            </Box>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              bgcolor: theme.palette.grey[100],
              color: theme.palette.semiBlack.main,
              py: '1rem',
            }}
          >
            <Typography sx={{ whiteSpace: 'pre-line' }}>
              {description}
            </Typography>
          </AccordionDetails>
        </Accordion>
      ) : (
        title && (
          <>
            <Typography
              variant='subtitle1'
              sx={{ fontSize: '1rem', mb: 1, fontWeight: 500 }}
            >
              {title}
              {(props.type === 'radio' || props.type === 'radio_vertical') &&
                !props.optional &&
                !question &&
                '*'}
            </Typography>
            {question && (
              <>
                <Typography variant='body1'>{`${question} ${props.optional ? '' : '*'}`}</Typography>
                <Box
                  sx={{
                    marginY: 1,
                    padding: '8px 16px',
                    bgcolor: theme => theme.palette.grey[100],
                  }}
                >
                  <Typography variant='caption'>{subtitle}</Typography>
                </Box>
              </>
            )}
          </>
        )
      )}

      {!description || title ? componentWrapper : null}
    </Box>
  );
};
